import { Gapcursor as TiptapGapcursor } from '@tiptap/extensions'
import { GapCursor } from '@tiptap/pm/gapcursor'
import type { ResolvedPos } from '@tiptap/pm/model'

/**
 * @see https://github.com/ProseMirror/prosemirror-gapcursor/blob/1.3.2/src/gapcursor.ts#L106
 */
function closedBefore($pos: ResolvedPos) {
  for (let d = $pos.depth; d >= 0; d--) {
    const index = $pos.index(d),
      parent = $pos.node(d)
    // At the start of this parent, look at next one
    if (index == 0) {
      if (parent.type.spec.isolating) return true
      continue
    }
    // See if the node before (or its first ancestor) is closed
    for (let before = parent.child(index - 1); ; before = before.lastChild!) {
      if (
        (before.childCount == 0 && !before.inlineContent) ||
        before.isAtom ||
        before.type.spec.isolating
      )
        return true
      if (before.inlineContent) return false
    }
  }
  // Hit start of document
  return true
}

/**
 * @see https://github.com/ProseMirror/prosemirror-gapcursor/blob/1.3.2/src/gapcursor.ts#L124
 */
function closedAfter($pos: ResolvedPos) {
  for (let d = $pos.depth; d >= 0; d--) {
    const index = $pos.indexAfter(d),
      parent = $pos.node(d)
    if (index == parent.childCount) {
      if (parent.type.spec.isolating) return true
      continue
    }
    for (let after = parent.child(index); ; after = after.firstChild!) {
      if (
        (after.childCount == 0 && !after.inlineContent) ||
        after.isAtom ||
        after.type.spec.isolating
      )
        return true
      if (after.inlineContent) return false
    }
  }
  return true
}

/**
 * Modifies Gapcursor.valid() to have special behavior for `centerJustify` nodes.
 */
Object.defineProperty(GapCursor, 'valid', {
  value: ($pos: ResolvedPos): boolean => {
    const parent = $pos.parent
    if (parent.isTextblock || !closedBefore($pos) || !closedAfter($pos)) {
      return false
    }
    const override = parent.type.spec.allowGapCursor
    if (override != null) {
      return override
    }

    // Support center-justified
    if ($pos.depth === 0) {
      if (closedBefore($pos) && $pos.nodeAfter?.type.name === 'centerJustify') {
        return false
      }
      if (closedAfter($pos) && $pos.nodeBefore?.type.name === 'centerJustify') {
        return false
      }
    }

    const deflt = parent.contentMatchAt($pos.index()).defaultType
    return deflt != null && deflt.isTextblock
  },
})

export const Gapcursor = TiptapGapcursor
